import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { useTheme } from "@material-ui/core/styles";
import useConnectionsStyles from "./connectionsStyles";
import useBaileysVersion from "./useBaileysVersion";
import useConnectionsSocket from "./useConnectionsSocket";
import { Paper, Box, Typography, Grid } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import {
  updateWhatsapp,
  deleteWhatsapp,
  cleanWhatsappSession,
  restartWhatsapp,
  startWhatsappSession,
} from "../../services/whatsapp.api";
import { DEFAULT_PROVIDER, DISCONNECT_REASONS } from "../../constants/whatsappProviders";
import { useTimezone } from "../../hooks/useTimezone";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

import AddConnectionCard from "./components/AddConnectionCard";
import ConnectionContextMenu from "./components/ConnectionContextMenu";
import ConnectionModals from "./components/ConnectionModals";
import ConnectionsHeader from "./components/ConnectionsHeader";
import ConnectionCard from "./components/ConnectionCard";
import WebFormCard from "./components/WebFormCard";
import WebFormContextMenu from "./components/WebFormContextMenu";
import { listWebForms, getWebForm, updateWebForm as updateWebFormApi } from "../../services/webForms.api";


const Connections = () => {
	const classes = useConnectionsStyles();
	const theme = useTheme();
	const baileysInfo = useBaileysVersion();
	const { formatDateTime } = useTimezone();
	const { user } = useContext(AuthContext);
	const { whatsApps, loading, refreshWhatsApps, dispatch } = useContext(WhatsAppsContext);
	const socketManager = useContext(SocketContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
	const [reconnectingWhatsApp, setReconnectingWhatsApp] = useState(null);
	const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
	const [disconnectingWhatsApp, setDisconnectingWhatsApp] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deletingWhatsApp, setDeletingWhatsApp] = useState(null);
	// ✅ ESTADO ELIMINADO: autoRefreshInProgress - Causaba bloqueo de la interfaz
	const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedMenuWhatsApp, setSelectedMenuWhatsApp] = useState(null);
	const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
	const [newConnectionProvider, setNewConnectionProvider] = useState(DEFAULT_PROVIDER);
	// Grilla de formularios web (doc 0417 §8) — segundo origen de datos de la
	// misma grilla, junto a `whatsApps`. No vive en un Context: a diferencia
	// de WhatsApps no hay eventos de socket que la mantengan viva, un fetch
	// al montar y tras cada acción alcanza.
	const [webForms, setWebForms] = useState([]);
	const [webFormsLoading, setWebFormsLoading] = useState(true);
	const [webFormMenuAnchorEl, setWebFormMenuAnchorEl] = useState(null);
	const [selectedMenuWebForm, setSelectedMenuWebForm] = useState(null);
	// Modal del Builder (doc 0417, corregido 2026-09-19: modal, no página —
	// mismo patrón que WhatsAppModal). `editingWebFormId=null` = modo "nuevo".
	const [webFormModalOpen, setWebFormModalOpen] = useState(false);
	const [editingWebFormId, setEditingWebFormId] = useState(null);
	// ✅ FASE 2: Estado para alertas de recursos (guardado para uso futuro, ej: dashboard)
	// eslint-disable-next-line no-unused-vars
	const [resourceAlerts, setResourceAlerts] = useState([]);
	
	// ✅ Flag para evitar toasts duplicados de errores QR
	const qrErrorToastShown = useRef(new Map()); // Key: whatsappId-errorCode, Value: timestamp

	// Retorno del OAuth de Instagram (Login oficial, 0379): feedback + refresh + limpiar la URL.
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const ig = params.get("ig");
		if (!ig) return;
		if (ig === "ok") {
			toast.success("Instagram conectado. Escribe un DM de prueba para confirmar.");
			if (typeof refreshWhatsApps === "function") refreshWhatsApps();
		} else {
			toast.error(`No se pudo conectar Instagram (${params.get("reason") || "error"}).`);
		}
		window.history.replaceState({}, "", window.location.pathname);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ✅ Refs para los estados que se usan dentro del socket useEffect.
	// Evita que el useEffect se re-ejecute (y re-registre listeners) cada vez que
	// qrModalOpen / selectedWhatsApp / reconnectModalOpen / reconnectingWhatsApp cambian.
	const qrModalOpenRef          = useRef(qrModalOpen);
	const selectedWhatsAppRef     = useRef(selectedWhatsApp);
	const reconnectModalOpenRef   = useRef(reconnectModalOpen);
	const reconnectingWhatsAppRef = useRef(reconnectingWhatsApp);
	useEffect(() => { qrModalOpenRef.current          = qrModalOpen;          }, [qrModalOpen]);
	useEffect(() => { selectedWhatsAppRef.current     = selectedWhatsApp;     }, [selectedWhatsApp]);
	useEffect(() => { reconnectModalOpenRef.current   = reconnectModalOpen;   }, [reconnectModalOpen]);
	useEffect(() => { reconnectingWhatsAppRef.current = reconnectingWhatsApp; }, [reconnectingWhatsApp]);

	// ✅ FASE 2: FUNCIÓN PARA MOSTRAR NOTIFICACIONES DE RECURSOS
	const showResourceNotification = useCallback((alert) => {
		const message = `${alert.message}\n` +
			`Sesiones: ${alert.currentSessions}/${alert.maxTheoretical} (${alert.utilizationPercent.toFixed(1)}%)\n` +
			`Impacto estimado: ~${(alert.impact.memoryMB / 1024).toFixed(1)} GB RAM, ~${alert.impact.cpuPercent.toFixed(1)}% CPU\n` +
			`${alert.impact.recommendation}`;
		
		switch (alert.level) {
			case 'info':
				toast.info(message, { autoClose: 5000 });
				break;
			case 'warning':
				toast.warning(message, { autoClose: 7000 });
				break;
			case 'error':
				toast.error(message, { autoClose: 10000 });
				break;
			case 'critical':
				toast.error(message, {
					autoClose: 15000,
					onClick: () => {
						// Opcional: abrir página de soporte o dashboard
						console.log('Abrir soporte para upgrade de servidor');
					}
				});
				break;
			default:
				toast.info(message, { autoClose: 5000 });
		}
	}, []);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);


  // ✅ FUNCIÓN PARA VERIFICAR ESTADO DE CONEXIONES PERIÓDICAMENTE (OPTIMIZADA)
  useEffect(() => {
    // ✅ VERIFICAR ESTADO CADA 120 SEGUNDOS (menos agresivo) PARA MANTENER SINCRONIZACIÓN
    const intervalId = setInterval(async () => {
      try {
        // ✅ SOLO REFRESCAR SI NO ESTÁ CARGANDO Y HAY CONEXIONES
        if (!loading && whatsApps && whatsApps.length > 0) {
          // ✅ LOG DE VERIFICACIÓN PERIÓDICA
          console.log("VERIFICACIÓN PERIÓDICA DE ESTADO DE CONEXIONES...");
          await refreshWhatsApps();
        }
      } catch (error) {
        console.error("Error en verificación periódica:", error);
      }
    }, 120000); // ✅ OPTIMIZADO: 120 segundos (menos agresivo)

    return () => {
      clearInterval(intervalId);
    };
  }, [loading, whatsApps, refreshWhatsApps]);

  // ✅ LISTENER PARA ACTUALIZACIÓN AUTOMÁTICA DESPUÉS DE CONEXIÓN EXITOSA
  useEffect(() => {
    const handleWhatsAppConnectionSuccess = async (event) => {
              console.log("EVENTO DE CONEXIÓN EXITOSA RECIBIDO:", event.detail);
      
      // ✅ ACTUALIZACIÓN SIMPLIFICADA - Sin bloqueo de interfaz
      
      // ✅ VERIFICAR SI YA SE ACTUALIZÓ RECIENTEMENTE (últimos 10 segundos)
      const now = Date.now();
      if (lastAutoRefresh && (now - lastAutoRefresh) < 10000) {
        console.log("ACTUALIZACIÓN AUTOMÁTICA RECIENTE DETECTADA, IGNORANDO...");
        return;
      }
      
      try {
        // ✅ ACTUALIZACIÓN INMEDIATA - Cambiado de 5 segundos a 1 segundo
        setTimeout(async () => {
          // ✅ LOG DE ACTUALIZACIÓN AUTOMÁTICA
          console.log("🚀 EJECUTANDO ACTUALIZACIÓN INMEDIATA DESPUÉS DE CONEXIÓN EXITOSA...");
          
          // ✅ ACTUALIZAR LISTA DE CONEXIONES
          await refreshWhatsApps();
          
          // ✅ ACTUALIZAR TIMESTAMP
          setLastAutoRefresh(Date.now());
          
          // ✅ CONFIRMACIÓN ELIMINADA - Evitar spam de notificaciones
          
        }, 1000); // 1 segundo - ACTUALIZACIÓN INMEDIATA
        
      } catch (error) {
        console.error("Error en actualización automática:", error);
        // ✅ NOTIFICACIÓN DE ERROR ELIMINADA - Evitar spam
      }
    };

    // ✅ AGREGAR LISTENER PARA EL EVENTO PERSONALIZADO
    window.addEventListener('whatsapp-connection-success', handleWhatsAppConnectionSuccess);

    // ✅ LIMPIAR LISTENER AL DESMONTAR
    return () => {
      window.removeEventListener('whatsapp-connection-success', handleWhatsAppConnectionSuccess);
    };
  }, [refreshWhatsApps, lastAutoRefresh]);

  // ✅ LISTENER PARA ACTUALIZACIÓN INMEDIATA DESDE QRCODEMODAL
  useEffect(() => {
    const handleRefreshConnections = async () => {
      console.log("🔄 Evento de actualización de conexiones recibido");
      try {
        // ✅ ACTUALIZACIÓN INMEDIATA SIN DELAY
        await refreshWhatsApps();
        console.log("✅ Conexiones actualizadas inmediatamente");
      } catch (error) {
        console.error("❌ Error actualizando conexiones:", error);
      }
    };

    // ✅ LISTENER PARA ACTUALIZACIÓN DE ESTADO LOCAL INMEDIATA
    const handleUpdateWhatsAppStatus = (event) => {
      const { whatsAppId, newStatus } = event.detail;
      console.log("🔄 Actualizando estado local inmediatamente:", whatsAppId, newStatus);
      
      // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE
      if (whatsApps && whatsApps.length > 0) {
        const updatedWhatsApps = whatsApps.map(wa => {
          if (wa.id === whatsAppId) {
            return {
              ...wa,
              status: newStatus.status,
              qrcode: newStatus.qrcode,
              updatedAt: newStatus.updatedAt
            };
          }
          return wa;
        });
        
        // ✅ ACTUALIZAR ESTADO LOCAL
        dispatch({ type: "REFRESH_WHATSAPPS", payload: updatedWhatsApps });
        console.log("✅ Estado local actualizado inmediatamente");
      }
    };

    // ✅ ESCUCHAR EVENTOS PERSONALIZADOS
    window.addEventListener('refreshConnections', handleRefreshConnections);
    window.addEventListener('updateWhatsAppStatus', handleUpdateWhatsAppStatus);

    return () => {
      window.removeEventListener('refreshConnections', handleRefreshConnections);
      window.removeEventListener('updateWhatsAppStatus', handleUpdateWhatsAppStatus);
    };
  }, [refreshWhatsApps, whatsApps, dispatch]);

  // ✅ FASE 1C: FUNCIÓN PARA LIMPIAR Y RECONECTAR SESIÓN (mover antes del useEffect)
  const handleCleanAndReconnect = useCallback(async (whatsAppId) => {
    try {
      const whatsapp = whatsApps.find(w => w.id === whatsAppId);
      if (!whatsapp) {
        toast.error("❌ No se encontró la conexión WhatsApp");
        return;
      }

      // Guard agnóstico: solo Baileys con sesión corrupta puede limpiar sesión local
      if (!whatsapp.canCleanSession) {
        toast.info("Esta acción no está disponible para este tipo de conexión.");
        return;
      }

      // ✅ MOSTRAR CONFIRMACIÓN
      const confirmed = window.confirm(
        `¿Desea limpiar la sesión corrupta de "${whatsapp.name}" y reconectar?\n\n` +
        `Esto eliminará:\n` +
        `- Datos de sesión corruptos\n` +
        `- Archivos de sesión antiguos\n` +
        `- Y creará una nueva sesión limpia`
      );

      if (!confirmed) return;

      // ✅ MOSTRAR LOADING
      toast.info("🧹 Limpiando sesión corrupta...", { autoClose: 2000 });

      // ✅ LLAMAR API PARA LIMPIAR SESIÓN
      const response = await cleanWhatsappSession(whatsAppId);

      if (response.status === 200 && response.data.success) {
        toast.success("✅ Sesión limpiada exitosamente. Reconexión iniciada...", { autoClose: 3000 });
        
        // ✅ ACTUALIZAR ESTADO DESPUÉS DE UN DELAY
        setTimeout(async () => {
          try {
            await refreshWhatsApps();
            
            // ✅ VERIFICAR SI NECESITA MOSTRAR QR
            setTimeout(() => {
              const updatedWhatsapp = whatsApps.find(w => w.id === whatsAppId);
              if (updatedWhatsapp && (updatedWhatsapp.hasQrCapability || updatedWhatsapp.status === 'DISCONNECTED')) {
                setSelectedWhatsApp(updatedWhatsapp);
                setQrModalOpen(true);
              }
            }, 2000);
          } catch (error) {
            console.error("Error actualizando estado:", error);
          }
        }, 3000);
      } else {
        toast.error("❌ Error al limpiar sesión. Intente nuevamente.");
      }
    } catch (error) {
      console.error("❌ Error limpiando sesión:", error);
      toast.error("❌ Error al limpiar sesión: " + (error.response?.data?.error || error.message));
    }
  }, [whatsApps, refreshWhatsApps]);

  // ✅ TODOS LOS LISTENERS SOCKET.IO — extraídos a hook dedicado
  useConnectionsSocket({
    socketManager,
    companyId: user.companyId,
    refreshWhatsApps,
    handleCleanAndReconnect,
    showResourceNotification,
    whatsApps,
    qrModalOpen,
    selectedWhatsApp,
    reconnectModalOpen,
    reconnectingWhatsApp,
    qrModalOpenRef,
    selectedWhatsAppRef,
    reconnectModalOpenRef,
    reconnectingWhatsAppRef,
    qrErrorToastShown,
    setQrModalOpen,
    setSelectedWhatsApp,
    setReconnectModalOpen,
    setReconnectingWhatsApp,
    setLastAutoRefresh,
    setResourceAlerts,
  });

  // ✅ FUNCIÓN PARA RECONECTAR SESIÓN WHATSAPP CON POPUP DE PROGRESO
  const handleReconnectWhatsApp = async (whatsAppId) => {
    try {
      console.log(`🔄 Iniciando reconexión para WhatsApp ${whatsAppId}`);
      
      // ✅ ENCONTRAR EL WHATSAPP A RECONECTAR
      const whatsapp = whatsApps.find(w => w.id === whatsAppId);
      if (!whatsapp) {
        toast.error("❌ No se encontró la conexión WhatsApp");
        return;
      }
      
      // ✅ CONFIGURAR ESTADO PARA EL MODAL DE PROGRESO
      setReconnectingWhatsApp(whatsapp);
      setReconnectModalOpen(true);
      
      // ✅ LLAMAR API PARA RECONECTAR (SIN NOTIFICACIONES MÚLTIPLES)
      const response = await restartWhatsapp(whatsAppId);
      
      if (response.status === 200) {
        console.log("✅ Reconexión iniciada exitosamente");
        
        // ✅ ACTUALIZAR ESTADO LOCAL DESPUÉS DE UN DELAY
        setTimeout(async () => {
          try {
            await refreshWhatsApps();
            console.log("✅ Estado actualizado después de reconexión");
          } catch (error) {
            console.error("Error actualizando estado:", error);
          }
        }, 3000);
        
        // ✅ VERIFICAR SI NECESITA MOSTRAR QR DESPUÉS DEL MODAL
        setTimeout(() => {
          const updatedWhatsapp = whatsApps.find(w => w.id === whatsAppId);
          if (updatedWhatsapp && updatedWhatsapp.hasQrCapability) {
            setSelectedWhatsApp(updatedWhatsapp);
            setQrModalOpen(true);
          }
        }, 5000);
      }
    } catch (error) {
      console.error("❌ Error reconectando WhatsApp:", error);
      // ✅ CERRAR MODAL DE PROGRESO EN CASO DE ERROR
      setReconnectModalOpen(false);
      setReconnectingWhatsApp(null);
      // ✅ MOSTRAR SOLO UNA NOTIFICACIÓN DE ERROR
      toast.error("❌ Error al reconectar WhatsApp. Intente nuevamente.");
    }
  };

  // ✅ FUNCIÓN ELIMINADA: handleReconnectComplete - La actualización automática ahora se maneja en el modal

  // ✅ FUNCIÓN PARA MANEJAR ÉXITO EN CREACIÓN/EDICIÓN (OPTIMIZADA)
  const handleWhatsAppSuccess = async () => {
    try {
      // ✅ Cerrar modal inmediatamente
      setWhatsAppModalOpen(false);
      
      // ✅ NOTIFICACIÓN DE ÉXITO ELIMINADA - Evitar spam
      
      // ✅ ACTUALIZACIÓN NO BLOQUEANTE - Usar setTimeout para no bloquear UI
      setTimeout(async () => {
        try {
          await refreshWhatsApps();
        } catch (error) {
          console.error("Error en actualización post-creación:", error);
        }
      }, 2000); // ✅ 2 segundos (menos agresivo)
      
    } catch (error) {
      console.error("Error al manejar éxito de WhatsApp:", error);
      toastError(error);
    }
  };

  // ✅ FUNCIÓN PARA MANEJAR ÉXITO EN ELIMINACIÓN (OPTIMIZADA)
  const handleDeleteSuccess = async (whatsAppId) => {
    try {
      // ✅ Cerrar modal de confirmación inmediatamente
      setConfirmModalOpen(false);
      setConfirmModalInfo(confirmationModalInitialState);
      
      // ✅ NOTIFICACIÓN DE ÉXITO ELIMINADA - Evitar spam
      
      // ✅ ACTUALIZACIÓN NO BLOQUEANTE - Usar setTimeout para no bloquear UI
      setTimeout(async () => {
        try {
          await refreshWhatsApps();
          console.log("ACTUALIZACIÓN POST-ELIMINACIÓN COMPLETADA");
        } catch (error) {
          console.error("Error en actualización post-eliminación:", error);
        }
      }, 2000); // ✅ 2 segundos (no bloqueante)
      
    } catch (error) {
      console.error("Error al manejar éxito de eliminación:", error);
      toastError(error);
    }
  };

	// ✅ FUNCIÓN ELIMINADA: handleStartWhatsAppSession - Ya no se usa después de eliminar botón "INTENTAR DE NUEVO"

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			// ✅ ENCONTRAR EL WHATSAPP PRIMERO
			const whatsapp = whatsApps.find(w => w.id === whatsAppId);
			if (!whatsapp) {
				toast.error("❌ No se encontró la conexión WhatsApp");
				return;
			}
			
			// ✅ ABRIR MODAL PRIMERO (antes de solicitar QR para que esté listo para recibir eventos)
			setSelectedWhatsApp(whatsapp);
			setQrModalOpen(true);
			
			// ✅ SOLICITAR GENERACIÓN DE QR (después de abrir modal)
			// Esto permite que el modal esté listo para recibir los eventos de socket
			await startWhatsappSession(whatsAppId);
			
			// ✅ REFRESCAR CONEXIONES EN SEGUNDO PLANO PARA ACTUALIZAR ESTADO
			setTimeout(async () => {
				try {
					await refreshWhatsApps();
				} catch (error) {
					console.error("Error actualizando estado:", error);
				}
			}, 1500);
			
		} catch (err) {
			console.error("Error al generar nuevo código QR:", err);
			toastError(err);
			// ✅ CERRAR MODAL SI HAY ERROR
			setQrModalOpen(false);
			setSelectedWhatsApp(null);
		}
	};

	const handleOpenWhatsAppModal = (provider = DEFAULT_PROVIDER) => {
		setNewConnectionProvider(provider);
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
			setConfirmModalOpen(true);
		}

		if (action === "delete") {
			// ✅ USAR NUEVO MODAL DE ELIMINACIÓN COMPLETA
			const whatsapp = whatsApps.find(w => w.id === whatsAppId);
			if (whatsapp) {
				setDeletingWhatsApp(whatsapp);
				setDeleteModalOpen(true);
			}
		}
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalOpen(false);
		setConfirmModalInfo(confirmationModalInitialState);
	};

	const handleDisconnectWhatsApp = async () => {
		try {
			console.log(`🔄 Iniciando desconexión para WhatsApp ${confirmModalInfo.whatsAppId}`);
			
			// ✅ ENCONTRAR EL WHATSAPP A DESCONECTAR
			const whatsapp = whatsApps.find(w => w.id === confirmModalInfo.whatsAppId);
			if (!whatsapp) {
				toast.error("❌ No se encontró la conexión WhatsApp");
				handleCloseConfirmModal();
				return;
			}
			
			// ✅ CERRAR MODAL DE CONFIRMACIÓN INMEDIATAMENTE
			handleCloseConfirmModal();
			
			// ✅ CONFIGURAR ESTADO PARA EL MODAL DE PROGRESO
			setDisconnectingWhatsApp(whatsapp);
			setDisconnectModalOpen(true);
			
      // ✅ DESCONECTAR WHATSAPP e indicar razón manual
      await updateWhatsapp(confirmModalInfo.whatsAppId, {
        status: "DISCONNECTED",
        lastDisconnectReason: DISCONNECT_REASONS.USER_DISCONNECT
      });
			
			console.log("✅ Desconexión iniciada exitosamente");
			
			// ✅ ACTUALIZAR ESTADO LOCAL DESPUÉS DE UN DELAY
			setTimeout(async () => {
				try {
					await refreshWhatsApps();
					console.log("✅ Estado actualizado después de desconexión");
				} catch (error) {
					console.error("Error actualizando estado:", error);
				}
			}, 2000);
			
		} catch (err) {
			console.error("❌ Error desconectando WhatsApp:", err);
			// ✅ CERRAR MODAL DE PROGRESO EN CASO DE ERROR
			setDisconnectModalOpen(false);
			setDisconnectingWhatsApp(null);
			// ✅ MOSTRAR SOLO UNA NOTIFICACIÓN DE ERROR
			toast.error("❌ Error al desconectar WhatsApp. Intente nuevamente.");
		}
	};

	const handleDeleteWhatsApp = async () => {
		try {
			// ✅ ELIMINAR WHATSAPP - Eliminar completamente de la BD
			await deleteWhatsapp(confirmModalInfo.whatsAppId);
			
			// ✅ USAR NUEVA FUNCIÓN DE ÉXITO
			await handleDeleteSuccess(confirmModalInfo.whatsAppId);
			
		} catch (err) {
			console.error("Error al eliminar WhatsApp:", err);
			toastError(err);
		}
	};

	// ✅ FUNCIÓN PARA ELIMINACIÓN COMPLETA CON PROGRESO
	const handleCompleteDelete = async (whatsAppId) => {
		try {
			console.log(`🗑️ Iniciando eliminación completa para WhatsApp ${whatsAppId}`);
			
			// ✅ LLAMAR API PARA ELIMINACIÓN COMPLETA
			const response = await deleteWhatsapp(whatsAppId);
			
			if (response.status === 200) {
				console.log("✅ Eliminación completa exitosa");
				
				// ✅ MOSTRAR ESTADÍSTICAS DE ELIMINACIÓN
				if (response.data.deletedStats) {
					const stats = response.data.deletedStats;
					console.log("📊 Estadísticas de eliminación:", stats);
					
					// ✅ MOSTRAR NOTIFICACIÓN CON ESTADÍSTICAS
					toast.success(
						`✅ Eliminación completa exitosa: ${stats.deletedTickets} tickets, ${stats.deletedContacts} contactos, ${stats.deletedMessages} mensajes, ${stats.deletedTicketTrackings} estadísticas, ${stats.deletedUserRatings} evaluaciones eliminados`
					);
				}
				
				return response.data; // Return complete response data
			}
		} catch (error) {
			console.error("❌ Error en eliminación completa:", error);
			toast.error("❌ Error al eliminar WhatsApp. Intente nuevamente.");
			throw error; // Re-throw to be caught by the modal's error handling
		}
	};

	// ✅ FUNCIÓN PARA MANEJAR COMPLETADO DE ELIMINACIÓN Y RECARGAR PÁGINA
	const handleDeleteComplete = () => {
		console.log("🔄 Eliminación completada, actualizando página...");
		window.location.reload();
	};

	const handleMenuOpen = (event, whatsApp) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuWhatsApp(whatsApp);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedMenuWhatsApp(null);
	};

	const handleMenuEdit = () => {
		if (selectedMenuWhatsApp) {
			handleEditWhatsApp(selectedMenuWhatsApp);
		}
		handleMenuClose();
	};

	const handleMenuDelete = () => {
		if (selectedMenuWhatsApp) {
			handleOpenConfirmationModal("delete", selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuRestart = () => {
		if (selectedMenuWhatsApp) {
			handleReconnectWhatsApp(selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuDisconnect = () => {
		if (selectedMenuWhatsApp) {
			handleOpenConfirmationModal("disconnect", selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuCleanSession = () => {
		if (selectedMenuWhatsApp) {
			handleCleanAndReconnect(selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};



	const fetchWebForms = useCallback(async () => {
		try {
			const { data } = await listWebForms();
			setWebForms(data ?? []);
		} catch (err) {
			toastError(err);
		} finally {
			setWebFormsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchWebForms();
	}, [fetchWebForms]);

	const handleWebFormMenuOpen = (event, webForm) => {
		setWebFormMenuAnchorEl(event.currentTarget);
		setSelectedMenuWebForm(webForm);
	};

	const handleWebFormMenuClose = () => {
		setWebFormMenuAnchorEl(null);
		setSelectedMenuWebForm(null);
	};

	const handleAddWebForm = () => {
		setEditingWebFormId(null);
		setWebFormModalOpen(true);
	};

	const handleEditWebForm = (webForm) => {
		setEditingWebFormId(webForm.id);
		setWebFormModalOpen(true);
	};

	const handleCloseWebFormModal = () => {
		setWebFormModalOpen(false);
		setEditingWebFormId(null);
	};

	// El modal NO se cierra solo al guardar (a diferencia de WhatsAppModal):
	// al crear, salta a la pestaña "Instalación" para mostrar el script — cerrar
	// de inmediato le taparía al usuario el momento "aha" del Builder de Kommo.
	// Solo se refresca la grilla; cerrar queda en manos del botón "Cerrar".
	const handleSaveWebForm = () => {
		fetchWebForms();
	};

	const handleWebFormMenuEdit = () => {
		if (selectedMenuWebForm) handleEditWebForm(selectedMenuWebForm);
		handleWebFormMenuClose();
	};

	const handleViewWebFormScript = () => {
		if (selectedMenuWebForm) {
			const backendUrl =
				window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
			const snippet = `<script src="${backendUrl}/api/v1/public/forms/embed.js" data-form="${selectedMenuWebForm.hash}" async></script>`;
			if (navigator.clipboard) {
				navigator.clipboard.writeText(snippet).then(
					() => toast.success("Script copiado. Pégalo en la landing del cliente."),
					() => toast.info(snippet)
				);
			} else {
				toast.info(snippet);
			}
		}
		handleWebFormMenuClose();
	};

	const handleManageWebFormDomains = () => {
		// La pestaña "Configuración" del mismo modal trae los dominios
		// permitidos — no hay un modal separado para esto.
		if (selectedMenuWebForm) handleEditWebForm(selectedMenuWebForm);
		handleWebFormMenuClose();
	};

	// Alternar activo/pausado requiere el formulario COMPLETO (el resumen de
	// la lista no trae fieldsJson/styleJson, y el SP de actualización los
	// exige todos) — mismo criterio que ya usa el Builder para este mismo botón.
	const handleToggleWebFormStatus = async () => {
		if (!selectedMenuWebForm) return;
		const target = selectedMenuWebForm;
		handleWebFormMenuClose();
		try {
			const { data: full } = await getWebForm(target.id);
			await updateWebFormApi(target.id, {
				name: full.name,
				fieldsJson: full.fieldsJson,
				styleJson: full.styleJson,
				queueId: full.queueId,
				tagName: full.tagName,
				redirectUrl: full.redirectUrl,
				redirectButtonLabel: full.redirectButtonLabel,
				confirmationMessage: full.confirmationMessage,
				status: full.status === "activo" ? "pausado" : "activo"
			});
			toast.success(full.status === "activo" ? "Formulario pausado." : "Formulario activado.");
			fetchWebForms();
		} catch (err) {
			toastError(err);
		}
	};

	const handleActionButtonClick = (whatsApp) => {
		// WABA (Meta) no usa QR: la acción siempre abre editar/ver la conexión.
		if (!whatsApp.usesQr) {
			handleEditWhatsApp(whatsApp);
		} else if (whatsApp.status === "qrcode") {
			handleOpenQrModal(whatsApp);
		} else if (whatsApp.status === "CONNECTED") {
			handleEditWhatsApp(whatsApp);
		} else {
			handleRequestNewQrCode(whatsApp.id);
		}
	};

	return (
		<MainContainer>
			<ConnectionModals
				confirmModalInfo={confirmModalInfo}
				confirmModalOpen={confirmModalOpen}
				onCloseConfirmModal={handleCloseConfirmModal}
				onDisconnectWhatsApp={handleDisconnectWhatsApp}
				onDeleteWhatsApp={handleDeleteWhatsApp}
				qrModalOpen={qrModalOpen}
				onCloseQrModal={handleCloseQrModal}
				selectedWhatsApp={selectedWhatsApp}
				whatsAppModalOpen={whatsAppModalOpen}
				onCloseWhatsAppModal={handleCloseWhatsAppModal}
				onSaveWhatsApp={handleWhatsAppSuccess}
				newConnectionProvider={newConnectionProvider}
				reconnectModalOpen={reconnectModalOpen}
				setReconnectModalOpen={setReconnectModalOpen}
				setReconnectingWhatsApp={setReconnectingWhatsApp}
				reconnectingWhatsApp={reconnectingWhatsApp}
				disconnectModalOpen={disconnectModalOpen}
				setDisconnectModalOpen={setDisconnectModalOpen}
				setDisconnectingWhatsApp={setDisconnectingWhatsApp}
				disconnectingWhatsApp={disconnectingWhatsApp}
				deleteModalOpen={deleteModalOpen}
				setDeleteModalOpen={setDeleteModalOpen}
				setDeletingWhatsApp={setDeletingWhatsApp}
				deletingWhatsApp={deletingWhatsApp}
				onCompleteDelete={handleCompleteDelete}
				onDeleteComplete={handleDeleteComplete}
				webFormModalOpen={webFormModalOpen}
				onCloseWebFormModal={handleCloseWebFormModal}
				editingWebFormId={editingWebFormId}
				onSaveWebForm={handleSaveWebForm}
			/>
			<ConnectionsHeader
				user={user}
				addMenuAnchorEl={addMenuAnchorEl}
				setAddMenuAnchorEl={setAddMenuAnchorEl}
				onAdd={handleOpenWhatsAppModal}
				onAddWebForm={handleAddWebForm}
			/>
			<Paper className={classes.mainPaper}>
				<ConnectionContextMenu
					anchorEl={menuAnchorEl}
					onClose={handleMenuClose}
					selectedWhatsApp={selectedMenuWhatsApp}
					onEdit={handleMenuEdit}
					onDelete={handleMenuDelete}
					onRestart={handleMenuRestart}
					onDisconnect={handleMenuDisconnect}
					onCleanSession={handleMenuCleanSession}
				/>
				<WebFormContextMenu
					anchorEl={webFormMenuAnchorEl}
					onClose={handleWebFormMenuClose}
					selectedWebForm={selectedMenuWebForm}
					onEdit={handleWebFormMenuEdit}
					onViewScript={handleViewWebFormScript}
					onToggleStatus={handleToggleWebFormStatus}
					onManageDomains={handleManageWebFormDomains}
				/>
				{loading || webFormsLoading ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
						<Typography>Cargando conexiones...</Typography>
					</Box>
				) : (
					<Grid container spacing={3} className={classes.connectionsGrid}>
						{whatsApps?.length > 0 ? (
							whatsApps.map(whatsApp => (
								<Grid item xs={12} sm={6} md={4} lg={3} key={whatsApp.id}>
									<ConnectionCard
										whatsApp={whatsApp}
										classes={classes}
										theme={theme}
										user={user}
										formatDateTime={formatDateTime}
										onMenuOpen={handleMenuOpen}
										onActionButtonClick={handleActionButtonClick}
										baileysVersion={baileysInfo?.baileysVersion}
										baileysUpdatedAt={baileysInfo?.baileysUpdatedAt}
									/>
								</Grid>
							))
						) : null}
						{webForms?.length > 0 &&
							webForms.map(webForm => (
								<Grid item xs={12} sm={6} md={4} lg={3} key={`webform-${webForm.id}`}>
									<WebFormCard
										webForm={webForm}
										classes={classes}
										theme={theme}
										user={user}
										formatDateTime={formatDateTime}
										onEdit={handleEditWebForm}
										onMenuOpen={handleWebFormMenuOpen}
									/>
								</Grid>
							))}
						<Grid item xs={12} sm={6} md={4} lg={3} key="add-connection">
							<AddConnectionCard
								onClick={() => handleOpenWhatsAppModal("baileys")}
								classes={classes}
								theme={theme}
							/>
						</Grid>
						{whatsApps?.length === 0 && webForms?.length === 0 && (
							<Grid item xs={12} key="no-connections-message">
								<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
									<Typography variant="h6" color="textSecondary">
										No hay conexiones configuradas. Haz clic en "Agregar Conexión" para crear una.
									</Typography>
								</Box>
							</Grid>
						)}
					</Grid>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Connections;